import type {
    MoveResourceResult,
    MoveResourceType,
    ProjectAccessLevel,
    ProjectAccessMap,
    ProjectActivityMeta,
    ProjectActivityModel,
    ProjectBaremetalSummary,
    ProjectModel,
    ProjectProduct,
    ProjectResourceCounts,
    ProjectResourcesModel,
    ProjectRole,
} from '../apis/v1/projects/model';

const ACCESS_RANK: Record<ProjectAccessLevel, number> = { none: 0, view: 1, manage: 2 };

export const PRODUCT_OF_RESOURCE: Record<MoveResourceType, ProjectProduct> = {
    instance: 'vm',
    baremetal: 'baremetal',
    container: 'container',
};

export const activeProjectStorageKey = (userId: number) => `xver-active-project:${userId}`;

/** Unknown or missing levels count as `none`. */
export const accessAtLeast = (
    have: ProjectAccessLevel | undefined,
    need: ProjectAccessLevel
): boolean => (ACCESS_RANK[have ?? 'none'] ?? 0) >= ACCESS_RANK[need];

/** `preferredId` while the caller is still a member, else their own default project, else the first. */
export function resolveActiveProjectId(
    projects: ProjectModel[],
    preferredId: string | null | undefined
): string | null {
    if (preferredId && projects.some((project) => project.id === preferredId)) {
        return preferredId;
    }
    const fallback =
        projects.find((project) => project.isDefault && project.role === 'owner') ?? projects[0];
    return fallback?.id ?? null;
}

export const isCrossOwner = (source: ProjectModel, target: ProjectModel) =>
    source.owner.id !== target.owner.id;

/**
 * Projects that resources of `types` can move into from `source`: the caller needs manage on every
 * product involved in both projects, and only the source owner may move across owners.
 */
export function movableTargets(
    projects: ProjectModel[],
    source: ProjectModel,
    types: MoveResourceType[]
): ProjectModel[] {
    const canManageAll = (project: ProjectModel) =>
        types.every((type) => accessAtLeast(project.access[PRODUCT_OF_RESOURCE[type]], 'manage'));

    if (types.length === 0 || !canManageAll(source)) return [];

    return projects.filter(
        (project) =>
            project.id !== source.id &&
            canManageAll(project) &&
            (!isCrossOwner(source, project) || source.role === 'owner')
    );
}

/**
 * Whether to offer "Move to project…" for `type` resources in `source`: the caller needs manage on
 * that product there, and either a target to move into or ownership of `source`, since an owner
 * can create the target from the move dialog.
 */
export const canOfferMove = (
    projects: ProjectModel[],
    source: ProjectModel,
    type: MoveResourceType
): boolean =>
    accessAtLeast(source.access[PRODUCT_OF_RESOURCE[type]], 'manage') &&
    (source.role === 'owner' || movableTargets(projects, source, [type]).length > 0);

/**
 * `placeholderData` for query keys shaped `[base, projectId, ...rest]`: keeps the previous data
 * only while the project is unchanged, so one project's rows never show under another.
 */
export const keepIfSameProject =
    (projectId: string | null) =>
    <T>(previousData: T | undefined, previousQuery?: { queryKey: readonly unknown[] }) =>
        previousQuery?.queryKey[1] === projectId ? previousData : undefined;

/** Non-zero entries of a `project_not_empty` 409 `remaining`, in display order. */
export function remainingSummary(remaining: ProjectResourceCounts | undefined) {
    if (!remaining) return [];
    return (['instances', 'baremetals', 'containers', 'blockVolumes'] as const).flatMap((key) => {
        const count = remaining[key];
        return count ? [{ key, count }] : [];
    });
}

const PRODUCT_LIST_PATHS = ['/cloud-compute', '/bare-metal-compute', '/container-engine'];
const NON_DETAIL_SEGMENTS = new Set(['deployment', 'security-groups']);

/** The product list a resource-detail route belongs to, or null when `pathname` is not one. */
export function productListPathFor(pathname: string): string | null {
    const [base, id] = pathname.split('/').filter(Boolean);
    const listPath = `/${base}`;
    return id && PRODUCT_LIST_PATHS.includes(listPath) && !NON_DETAIL_SEGMENTS.has(id)
        ? listPath
        : null;
}

/** Project and member Selects turn searchable past this many options. */
export const SELECT_SEARCHABLE_AFTER = 7;
export const PROJECT_NAME_MAX = 64;
export const PROJECT_DESCRIPTION_MAX = 500;

const runeLength = (value: string) => [...value].length;

/** Mirrors the server rules: name 1..64 characters after trimming, description at most 500. */
export function validateProjectFields({
    name,
    description,
}: {
    name: string;
    description: string;
}) {
    const trimmed = name.trim();
    const errors: { name?: 'required' | 'tooLong'; description?: 'tooLong' } = {};
    if (!trimmed) errors.name = 'required';
    else if (runeLength(trimmed) > PROJECT_NAME_MAX) errors.name = 'tooLong';
    if (runeLength(description.trim()) > PROJECT_DESCRIPTION_MAX) errors.description = 'tooLong';
    return errors;
}

export type ProjectTab = 'general' | 'members' | 'resources' | 'billing' | 'activity' | 'danger';

/** Billing is shown only to the owner and to the payer or nominee. */
export function projectTabsFor(project: ProjectModel, viewerId: number | undefined): ProjectTab[] {
    const showBilling =
        project.role === 'owner' || (viewerId !== undefined && project.payer.userId === viewerId);
    return [
        'general',
        'members',
        'resources',
        ...(showBilling ? ['billing' as const] : []),
        'activity',
        'danger',
    ];
}

/** `value` when it is one of `available`, else the first available tab. */
export const parseProjectTab = (value: string | null, available: readonly ProjectTab[]) =>
    available.find((tab) => tab === value) ?? available[0];

/** Whether a viewer with `viewerRole` may edit or remove a member whose role is `targetRole`. */
export const canManageMember = (viewerRole: ProjectRole, targetRole: ProjectRole) =>
    targetRole !== 'owner' &&
    (viewerRole === 'owner' || (viewerRole === 'admin' && targetRole === 'member'));

/** Member access presets offered when inviting or editing; `billing` is for someone who only pays. */
export const ACCESS_PRESETS = {
    viewer: { vm: 'view', baremetal: 'view', container: 'view', block_storage: 'view' },
    operator: { vm: 'manage', baremetal: 'view', container: 'manage', block_storage: 'manage' },
    billing: { vm: 'none', baremetal: 'none', container: 'none', block_storage: 'none' },
} as const satisfies Record<string, ProjectAccessMap>;

export type AccessPreset = keyof typeof ACCESS_PRESETS;

/** Every product a member's access map covers, in display order. */
export const PROJECT_PRODUCTS = [
    'vm',
    'baremetal',
    'container',
    'block_storage',
] as const satisfies readonly ProjectProduct[];

/** The preset `access` matches exactly, else `custom`. */
export const presetFor = (access: ProjectAccessMap): AccessPreset | 'custom' =>
    (Object.keys(ACCESS_PRESETS) as AccessPreset[]).find((preset) =>
        PROJECT_PRODUCTS.every((product) => ACCESS_PRESETS[preset][product] === access[product])
    ) ?? 'custom';

/** Roles the viewer may grant when inviting or editing; an admin cannot grant admin. */
export const grantableRoles = (viewerRole: ProjectRole): Exclude<ProjectRole, 'owner'>[] =>
    viewerRole === 'owner' ? ['member', 'admin'] : viewerRole === 'admin' ? ['member'] : [];

/** Selection key shared by resource tables and the move modal. */
export const resourceKey = (type: MoveResourceType, id: string | number) => `${type}:${id}`;

/**
 * Fills each result's blank `name` (the server has none for a resource it could not find) from
 * the item the user picked, falling back to the id.
 */
export const namedMoveResults = (
    results: readonly MoveResourceResult[],
    picked: readonly { type: MoveResourceType; id: string; name: string }[]
): MoveResourceResult[] =>
    results.map((result) =>
        result.name
            ? result
            : {
                  ...result,
                  name:
                      picked.find((item) => item.type === result.type && item.id === result.id)
                          ?.name || result.id,
              }
    );

/** How many of `keys` are in `selected`, as the select-all checkbox shows it. */
export function selectionState(keys: readonly string[], selected: ReadonlySet<string>) {
    const count = keys.filter((key) => selected.has(key)).length;
    if (count === 0) return 'none';
    return count === keys.length ? 'all' : 'some';
}

const PAYMENT_PERIODS: ProjectBaremetalSummary['paymentPeriod'][] = ['weekly', 'monthly', 'yearly'];

/**
 * Client-side estimate from a resources listing: VM hourly prices summed, bare-metal prepaid
 * prices summed per payment period. Containers are usage-billed and not included.
 */
export function billingEstimate(resources: ProjectResourcesModel | undefined) {
    const instances = resources?.instances ?? [];
    const baremetals = resources?.baremetals ?? [];
    return {
        instanceCount: instances.length,
        hourly: instances.reduce((sum, instance) => sum + (instance.priceHourly || 0), 0),
        baremetal: PAYMENT_PERIODS.flatMap((period) => {
            const inPeriod = baremetals.filter((baremetal) => baremetal.paymentPeriod === period);
            if (inPeriod.length === 0) return [];
            const total = inPeriod.reduce(
                (sum, baremetal) => sum + (baremetal.pricePerPeriod || 0),
                0
            );
            return [{ period, count: inPeriod.length, total }];
        }),
    };
}

/**
 * Who pays for new resources in `project`: the accepted payer, else the owner. Null when there is
 * an accepted payer whose name the caller may not see (members other than owner, admin, payer).
 */
export function billedToName(project: ProjectModel): string | null {
    if (project.payer.status !== 'accepted') return project.owner.name;
    return project.payer.name ?? null;
}

/** Which transfer consequences apply to these resources; unknown container counts (`null`) count as some. */
export const transferNotices = (resources: ProjectResourceCounts) => ({
    privateIp: resources.instances > 0,
    containers: resources.containers !== 0,
    prepaid: resources.baremetals > 0,
});

/**
 * Whether an accepted payer keeps paying after `project` is transferred to `recipientId`: the
 * transfer clears the payer only when it is the old or the new owner. A payer id hidden from the
 * viewer is never the viewer's own.
 */
export const transferKeepsPayer = (project: ProjectModel, recipientId: number | undefined) =>
    project.payer.status === 'accepted' &&
    project.payer.userId !== project.owner.id &&
    project.payer.userId !== recipientId;

/**
 * Switcher, chip and move-target label. A shared project also names its owner, since every
 * account has a project called "Default".
 */
export const projectOptionLabel = (
    project: Pick<ProjectModel, 'name' | 'role' | 'owner'>,
    translate: (key: 'switcher.sharedLabel', values: { name: string; owner: string }) => string
) =>
    project.role === 'owner' || !project.owner.name
        ? project.name
        : translate('switcher.sharedLabel', { name: project.name, owner: project.owner.name });

/** Resource-detail errors that retrying or polling with the same project cannot fix. */
export const isScopeTerminalError = (error: unknown) => {
    const status = (error as { status?: number } | null | undefined)?.status;
    return status === 403 || status === 404;
};

/**
 * Projects to try, in order, when a container 404s under the active project or the active project
 * gives no container access.
 */
export const containerProbeCandidates = (
    projects: ProjectModel[],
    activeProjectId: string | null
) =>
    activeProjectId
        ? projects.filter(
              (project) =>
                  project.id !== activeProjectId && accessAtLeast(project.access.container, 'view')
          )
        : [];

/**
 * The first of `candidates` whose `fetch` resolves. A 403/404 means "not in this one"; any other
 * error is rethrown so the caller can offer a retry. Stops with undefined once `isCancelled()`.
 */
export async function firstProjectHolding<P>(
    candidates: readonly P[],
    fetch: (candidate: P) => Promise<unknown>,
    isCancelled: () => boolean
): Promise<P | undefined> {
    for (const candidate of candidates) {
        if (isCancelled()) return undefined;
        try {
            await fetch(candidate);
            return candidate;
        } catch (error) {
            if (!isScopeTerminalError(error)) throw error;
        }
    }
    return undefined;
}

const SERVER_DETAIL_CODES: ReadonlySet<string> = new Set(['owner_ineligible', 'invalid_request']);
const OWNER_INELIGIBLE_REASONS: ReadonlySet<string> = new Set([
    'account',
    'vm_limit',
    'container_engine',
    'owner_changed',
]);
const OWNER_INELIGIBLE_SELF_REASONS: ReadonlySet<string> = new Set([
    'account',
    'vm_limit',
    'container_engine',
]);

/**
 * Text for a known project error `code`: `errors.owner_ineligible_<reason>` (with `limit`) when
 * `owner_ineligible` carries a known `reason`, the server's message for codes whose reason
 * varies, else `translate('errors.<code>')`. Undefined when there is no code. Pass `self` when
 * the viewer is the new owner (accepting an ownership offer) to get the `_self` wording.
 */
export function projectErrorText(
    code: string | undefined,
    serverMessage: string | undefined,
    translate: (key: string, options?: { limit?: unknown }) => string,
    { reason, limit, self }: { reason?: unknown; limit?: unknown; self?: boolean } = {}
): string | undefined {
    if (!code) return undefined;
    if (
        code === 'owner_ineligible' &&
        typeof reason === 'string' &&
        OWNER_INELIGIBLE_REASONS.has(reason)
    ) {
        const suffix = self && OWNER_INELIGIBLE_SELF_REASONS.has(reason) ? '_self' : '';
        return translate(`errors.owner_ineligible_${reason}${suffix}`, { limit });
    }
    const detail = serverMessage?.trim();
    return detail && SERVER_DETAIL_CODES.has(code) ? detail : translate(`errors.${code}`);
}

/**
 * Label for a member or invitee row. The server leaves `name` empty for nameless accounts only
 * when it also sends `email` (owner/admin viewers); everyone else gets a masked-email name.
 */
export const memberLabel = (member: { name?: string; email?: string }): string =>
    member.name || member.email || '';

/** Select option label: "Name (email)" when both are known, else whichever one is. */
export const memberOptionLabel = (member: { name?: string; email?: string }): string =>
    member.name && member.email ? `${member.name} (${member.email})` : memberLabel(member);

/**
 * Actor of a VM activity row: `email` reaches only the owner and admins, `actor_name` everyone.
 * A row with `actor_id > 0` but neither (a nameless user seen by a viewer) is `someoneLabel`.
 */
export const activityActor = (
    item: { email?: string; actor_name?: string; actor_id?: number },
    systemLabel: string,
    someoneLabel: string
) => item.email || item.actor_name || ((item.actor_id ?? 0) > 0 ? someoneLabel : systemLabel);

/**
 * Who a project activity row names as its actor. The server blanks the payer's name on payer
 * rows for viewers who may not see it; `payer.lapsed` and rows without a user are the system.
 */
export function activityActorLabel(
    item: Pick<ProjectActivityModel, 'action' | 'actor'>,
    translate: (key: 'activity.someone' | 'activity.system') => string
): string {
    if (item.actor?.name) return item.actor.name;
    const isPerson =
        (item.actor?.id ?? 0) > 0 ||
        (item.action.startsWith('payer.') && item.action !== 'payer.lapsed');
    return translate(isPerson ? 'activity.someone' : 'activity.system');
}

const MOVE_DETAIL = /^(instance|baremetal|container) ย้าย(มาจาก|ไปยัง)โปรเจกต์ (.+)$/su;
const ACCESS_DETAIL =
    /^role=(owner|admin|member) vm=(none|view|manage) baremetal=(none|view|manage) container=(none|view|manage)(?: block_storage=(none|view|manage))?$/;
const MOVE_ACTIONS: ReadonlySet<string> = new Set(['resource.moved', 'resource.owner_changed']);
const ACCESS_ACTIONS: ReadonlySet<string> = new Set([
    'member.invited',
    'member.updated',
    'invitation.accepted',
]);

export type ActivityDetail = ProjectActivityMeta | { kind: 'text'; text: string };

/**
 * What to show under a project activity row: the server's `meta`, else `meta` parsed from the
 * detail formats xver-api writes. A row whose action has a structured detail but no parseable
 * one gets null, never the server's untranslated text; other actions get their raw `detail`.
 */
export function activityDetail(
    item: Pick<ProjectActivityModel, 'action' | 'detail' | 'meta'>
): ActivityDetail | null {
    if (item.meta) return item.meta;
    const detail = item.detail?.trim() ?? '';
    if (MOVE_ACTIONS.has(item.action)) {
        const match = MOVE_DETAIL.exec(detail);
        return match
            ? {
                  kind: 'move',
                  resourceType: match[1] as MoveResourceType,
                  direction: match[2] === 'มาจาก' ? 'in' : 'out',
                  projectName: match[3],
              }
            : null;
    }
    if (ACCESS_ACTIONS.has(item.action)) {
        const match = ACCESS_DETAIL.exec(detail);
        if (!match) return null;
        const [, role, vm, baremetal, container, blockStorage] = match as unknown as [
            string,
            ProjectRole,
            ProjectAccessLevel,
            ProjectAccessLevel,
            ProjectAccessLevel,
            ProjectAccessLevel | undefined,
        ];
        return {
            kind: 'access',
            role,
            access: { vm, baremetal, container, block_storage: blockStorage ?? 'none' },
        };
    }
    if (item.action.startsWith('privnet.')) {
        return detail && detail !== '-' ? { kind: 'privateIp', privateIp: detail } : null;
    }
    return detail ? { kind: 'text', text: detail } : null;
}

/**
 * Focus `target()` once the re-render that follows an awaited invalidation has committed
 * (TanStack notifies observers on a zero timeout). Use when a mutation removed the focused control.
 */
export const focusAfterRender = (target: () => HTMLElement | null | undefined) => {
    setTimeout(() => target()?.focus(), 0);
};
